import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const MENU_DATA = [
  {
    title: 'Registration & OPD',
    icon: 'bi-person-plus',
    links: [
      { name: 'New Patient Registration', path: '/clinical/register', icon: 'bi-person-plus-fill', shortcut: 'Ctrl+P' },
      { name: 'OPD List', path: '/clinical/opd-list', icon: 'bi-list-ul', shortcut: 'Ctrl+O' },
    ]
  },
  {
    title: 'IPD & Wards',
    icon: 'bi-hospital',
    links: [
      { name: 'IPD List', path: '/clinical/ipd-list', icon: 'bi-list-ul', shortcut: 'Ctrl+I' },
      { name: 'Room Booking', path: '/clinical/room-booking', icon: 'bi-door-open', shortcut: 'Ctrl+B' },
      { name: 'Service Details', path: '/clinical/service-details', icon: 'bi-file-medical', shortcut: 'Ctrl+S' },
    ]
  },
  {
    title: 'Billing & Discharge',
    icon: 'bi-receipt',
    links: [
      { name: 'Advance Payment', path: '/clinical/advance', icon: 'bi-cash-coin', shortcut: 'Ctrl+A' },
      { name: 'Discharge Bill', path: '/clinical/discharge-bill', icon: 'bi-receipt-cutoff', shortcut: 'Ctrl+D' },
      { name: 'Discharge List', path: '/clinical/discharge-list', icon: 'bi-list-check' },
    ]
  }
];

// Path → page name mapping (breadcrumb ke liye)
const PATH_MAP = {
  'register': 'New Patient Registration',
  'opd-list': 'OPD List',
  'ipd-list': 'IPD List',
  'room-booking': 'Room Booking',
  'service-details': 'Service Details',
  'advance': 'Advance Payment',
  'discharge-bill': 'Discharge Bill',
  'discharge-list': 'Discharge List',
};

const ClinicalNavbar = () => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userInfo, setUserInfo] = useState({ name: '', role: '' });
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // User from session
  useEffect(() => {
    const name = sessionStorage.getItem('userName') || 'Admin User';
    const role = sessionStorage.getItem('userRole') || 'Receptionist';
    setUserInfo({ name, role });
  }, []);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setActiveMenu(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const toggleMenu = (index) => setActiveMenu(activeMenu === index ? null : index);
  const handleLinkClick = (path) => { setActiveMenu(null); setMobileOpen(false); navigate(path); };

  // Lock body scroll when mobile drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Current page name
  const getCurrentPage = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    return PATH_MAP[last] || 'Reception Desk';
  };

  const formatDate = (date) =>
    date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

  const formatTime = (date) =>
    date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

  return (
    <div className="erp-nav-container" ref={navRef}>
      {/* Hamburger (mobile only) */}
      <button className="erp-hamburger" onClick={() => setMobileOpen(true)} title="Menu" aria-label="Open menu">
        <i className="bi bi-list"></i>
      </button>

      {/* Brand */}
      <div className="erp-brand" onClick={() => navigate('/dashboard')} title="Exit to Main HMS">
        <div className="back-icon"><i className="bi bi-grid-3x3-gap-fill"></i></div>
        <span className="brand-text">Dashboard</span>
        <i className="bi bi-box-arrow-up-right exit-arrow"></i>
      </div>

      <div className="erp-divider"></div>

      {/* Module Title */}
      <div className="module-title-wrap">
        <div className="module-icon-circle">
          <i className="bi bi-hospital"></i>
        </div>
        <div className="module-meta">
          <div className="module-name">Hospital</div>
          <div className="module-sub">Reception Desk</div>
        </div>
      </div>

      <div className="erp-divider"></div>

      {/* Menus */}
      <div className="erp-menu-bar">
        {MENU_DATA.map((menu, idx) => (
          <div className="erp-menu-item" key={idx}>
            <button
              className={`erp-menu-btn ${activeMenu === idx ? 'active' : ''}`}
              onClick={() => toggleMenu(idx)}
              onMouseEnter={() => activeMenu !== null && setActiveMenu(idx)}
            >
              <i className={`bi ${menu.icon}`}></i>
              <span>{menu.title}</span>
              <i className="bi bi-chevron-down chev-icon"></i>
            </button>

            {activeMenu === idx && (
              <div className="erp-dropdown">
                <div className="erp-dropdown-header">
                  <i className={`bi ${menu.icon}`}></i>
                  <span>{menu.title}</span>
                  <span className="header-count">{menu.links.length}</span>
                </div>
                <div className="erp-dropdown-body">
                  {menu.links.map((link, lIdx) => (
                    <div
                      className="erp-dropdown-link"
                      key={lIdx}
                      onClick={() => handleLinkClick(link.path)}
                    >
                      <i className={`bi ${link.icon} dropdown-item-icon`}></i>
                      <span className="link-text">{link.name}</span>
                      {link.shortcut && <span className="link-shortcut">{link.shortcut}</span>}
                      <i className="bi bi-arrow-right link-arrow"></i>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* RIGHT SIDE — Merged Info Block */}
      <div className="erp-nav-right">
        {/* Current Page Indicator */}
        <div className="nav-current-page" title="Current Page">
          <i className="bi bi-geo-alt-fill"></i>
          <span>{getCurrentPage()}</span>
        </div>

        <div className="nav-vert-divider"></div>

        {/* Date + Time Combined */}
        <div className="nav-clock">
          <i className="bi bi-clock"></i>
          <div className="clock-meta">
            <div className="clock-time">{formatTime(currentTime)}</div>
            <div className="clock-date">{formatDate(currentTime)}</div>
          </div>
        </div>

        <div className="nav-vert-divider"></div>

        {/* User Chip */}
        <div className="nav-user-chip" title={userInfo.name}>
          <div className="nav-user-avatar">{userInfo.name.charAt(0).toUpperCase()}</div>
          <div className="nav-user-meta">
            <div className="nav-user-name">{userInfo.name}</div>
            <div className="nav-user-role">{userInfo.role}</div>
          </div>
        </div>
      </div>

      {/* ============ MOBILE DRAWER ============ */}
      {mobileOpen && (
        <div className="mobile-drawer-overlay" onClick={() => setMobileOpen(false)}>
          <div className="mobile-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-drawer-head">
              <div className="md-module">
                <div className="md-module-icon"><i className="bi bi-hospital"></i></div>
                <div>
                  <div className="md-module-name">Clinical</div>
                  <div className="md-module-sub">Reception Desk</div>
                </div>
              </div>
              <button className="md-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="mobile-drawer-body">
              <button className="md-exit" onClick={() => { setMobileOpen(false); navigate('/dashboard'); }}>
                <i className="bi bi-box-arrow-left"></i> Exit to Main Dashboard
              </button>

              {MENU_DATA.map((menu, idx) => (
                <div className="md-section" key={idx}>
                  <div className="md-section-title">
                    <i className={`bi ${menu.icon}`}></i> {menu.title}
                  </div>
                  {menu.links.map((link, lIdx) => (
                    <div className="md-link" key={lIdx} onClick={() => handleLinkClick(link.path)}>
                      <i className={`bi ${link.icon}`}></i>
                      <span>{link.name}</span>
                      {link.shortcut && <span className="md-shortcut">{link.shortcut}</span>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* ===========================================================
           THEME TOKENS — Light Slate + Teal (clinical/reception)
           =========================================================== */
        .erp-nav-container {
          --nav-bg: #ffffff;
          --nav-bg-soft: #f8fafc;
          --nav-border: #e2e8f0;
          --nav-accent: #0d9488;        /* teal-600 */
          --nav-accent-dark: #0f766e;   /* teal-700 */
          --nav-accent-soft: #f0fdfa;   /* teal-50  */
          --nav-accent-ring: rgba(13, 148, 136, 0.25);
          --nav-text: #1e293b;          /* slate-800 */
          --nav-text-soft: #64748b;     /* slate-500 */
          --nav-text-muted: #94a3b8;    /* slate-400 */

          height: 56px;
          background: var(--nav-bg);
          border-bottom: 1px solid var(--nav-border);
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06),
                      0 1px 2px rgba(15, 23, 42, 0.04);
          display: flex;
          align-items: center;
          padding: 0 14px;
          color: var(--nav-text);
          user-select: none;
          z-index: 100;
          font-family: 'Inter', system-ui, sans-serif;
          position: relative;
        }
        /* accent strip at very top for brand identity */
        .erp-nav-container::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #14b8a6 0%, #0d9488 50%, #0891b2 100%);
        }

        /* Hamburger — hidden on desktop, shown on mobile */
        .erp-hamburger {
          display: none;
          width: 38px; height: 38px;
          border: 1px solid var(--nav-border);
          background: var(--nav-bg-soft);
          border-radius: 9px;
          color: var(--nav-text);
          font-size: 20px;
          align-items: center; justify-content: center;
          cursor: pointer; margin-right: 10px;
          transition: all 0.2s;
        }
        .erp-hamburger:hover { background: var(--nav-accent-soft); color: var(--nav-accent); }

        /* Mobile drawer */
        .mobile-drawer-overlay {
          position: fixed; inset: 0;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(2px);
          z-index: 2000;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .mobile-drawer {
          position: absolute; top: 0; left: 0; bottom: 0;
          width: 84%; max-width: 320px;
          background: #ffffff;
          box-shadow: 4px 0 30px rgba(15, 23, 42, 0.25);
          display: flex; flex-direction: column;
          animation: drawerIn 0.25s ease;
        }
        @keyframes drawerIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .mobile-drawer-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px; border-bottom: 1px solid var(--nav-border);
          background: linear-gradient(180deg, var(--nav-accent-soft) 0%, #ffffff 100%);
        }
        .md-module { display: flex; align-items: center; gap: 10px; }
        .md-module-icon {
          width: 38px; height: 38px; border-radius: 10px;
          background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 18px; box-shadow: 0 2px 6px rgba(13,148,136,0.35);
        }
        .md-module-name { font-size: 15px; font-weight: 800; color: var(--nav-text); }
        .md-module-sub {
          font-size: 9px; font-weight: 700; color: var(--nav-text-muted);
          text-transform: uppercase; letter-spacing: 0.6px;
        }
        .md-close {
          width: 34px; height: 34px; border-radius: 8px;
          border: 1px solid var(--nav-border); background: #fff;
          color: var(--nav-text-soft); font-size: 15px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
        }
        .md-close:hover { background: #f1f5f9; }
        .mobile-drawer-body { flex: 1; overflow-y: auto; padding: 12px; }
        .md-exit {
          width: 100%; display: flex; align-items: center; gap: 10px;
          padding: 11px 12px; margin-bottom: 14px;
          background: var(--nav-accent-soft); color: var(--nav-accent-dark);
          border: 1px solid #99f6e4; border-radius: 9px;
          font-size: 13px; font-weight: 700; cursor: pointer;
        }
        .md-section { margin-bottom: 16px; }
        .md-section-title {
          display: flex; align-items: center; gap: 8px;
          font-size: 10px; font-weight: 800; color: var(--nav-accent-dark);
          text-transform: uppercase; letter-spacing: 0.6px;
          padding: 6px 8px; margin-bottom: 4px;
        }
        .md-link {
          display: flex; align-items: center; gap: 12px;
          padding: 11px 12px; border-radius: 9px;
          font-size: 14px; font-weight: 500; color: #334155;
          cursor: pointer; transition: all 0.15s;
        }
        .md-link i { font-size: 16px; color: var(--nav-text-muted); width: 18px; text-align: center; }
        .md-link span:not(.md-shortcut) { flex: 1; }
        .md-link:active, .md-link:hover {
          background: var(--nav-accent-soft); color: var(--nav-accent-dark);
        }
        .md-link:hover i { color: var(--nav-accent); }
        .md-shortcut {
          font-size: 10px; background: #f1f5f9; padding: 2px 7px;
          border-radius: 5px; color: var(--nav-text-soft);
          font-family: 'Consolas', monospace; font-weight: 600;
          border: 1px solid var(--nav-border);
        }

        /* Brand */
        .erp-brand {
          display: flex; align-items: center; gap: 8px;
          cursor: pointer; padding: 6px 12px 6px 6px;
          border-radius: 9px; transition: all 0.2s;
          background: var(--nav-bg-soft);
          border: 1px solid var(--nav-border);
        }
        .erp-brand:hover {
          background: var(--nav-accent-soft);
          border-color: rgba(13, 148, 136, 0.3);
        }
        .erp-brand:hover .exit-arrow {
          opacity: 1; transform: translateX(2px);
        }

        .back-icon {
          width: 28px; height: 28px;
          background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
          border-radius: 7px;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; color: white;
          box-shadow: 0 2px 6px rgba(13, 148, 136, 0.35);
        }
        .brand-text {
          font-size: 13px; font-weight: 700;
          color: var(--nav-text); letter-spacing: 0.2px;
        }
        .exit-arrow {
          font-size: 10px; color: var(--nav-accent);
          opacity: 0.5; transition: all 0.2s;
        }

        /* Module Title */
        .module-title-wrap {
          display: flex; align-items: center; gap: 10px;
        }
        .module-icon-circle {
          width: 34px; height: 34px;
          background: var(--nav-accent-soft);
          border: 1px solid rgba(13, 148, 136, 0.25);
          border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          color: var(--nav-accent); font-size: 15px;
        }
        .module-meta {
          display: flex; flex-direction: column; line-height: 1.15;
        }
        .module-name {
          font-size: 13.5px; font-weight: 800;
          color: var(--nav-text); letter-spacing: 0.1px;
        }
        .module-sub {
          font-size: 9px; font-weight: 700;
          color: var(--nav-text-muted); text-transform: uppercase;
          letter-spacing: 0.7px; margin-top: 1px;
        }

        .erp-divider {
          width: 1px; height: 30px;
          background: var(--nav-border);
          margin: 0 12px;
        }

        /* Menu Bar */
        .erp-menu-bar {
          display: flex; align-items: center;
          gap: 2px; flex: 1;
        }
        .erp-menu-item { position: relative; }

        .erp-menu-btn {
          background: transparent; border: none;
          color: var(--nav-text-soft); font-size: 13px;
          font-weight: 600; padding: 8px 12px;
          border-radius: 8px; cursor: pointer;
          display: flex; align-items: center; gap: 7px;
          transition: all 0.18s ease; position: relative;
        }
        .erp-menu-btn i:first-child {
          font-size: 14px; color: var(--nav-text-muted);
          transition: color 0.2s;
        }
        .erp-menu-btn span { line-height: 1; }

        .chev-icon {
          font-size: 9px !important; opacity: 0.5;
          margin-left: 2px; transition: transform 0.2s;
        }

        .erp-menu-btn:hover {
          background: var(--nav-bg-soft); color: var(--nav-text);
        }
        .erp-menu-btn:hover i:first-child { color: var(--nav-accent); }
        .erp-menu-btn:hover .chev-icon { opacity: 1; }

        .erp-menu-btn.active {
          background: var(--nav-accent-soft); color: var(--nav-accent-dark);
          box-shadow: inset 0 0 0 1px rgba(13, 148, 136, 0.3);
        }
        .erp-menu-btn.active i:first-child { color: var(--nav-accent); }
        .erp-menu-btn.active .chev-icon {
          transform: rotate(180deg); opacity: 1;
        }
        .erp-menu-btn.active::after {
          content: ''; position: absolute;
          bottom: -3px; left: 50%;
          transform: translateX(-50%);
          width: 55%; height: 2.5px;
          background: var(--nav-accent); border-radius: 2px;
        }

        /* Dropdown */
        .erp-dropdown {
          position: absolute; top: calc(100% + 8px); left: 0;
          background: #ffffff; border: 1px solid var(--nav-border);
          box-shadow: 0 20px 40px -12px rgba(15, 23, 42, 0.18),
                      0 8px 16px -8px rgba(15, 23, 42, 0.1);
          border-radius: 12px; min-width: 256px;
          z-index: 1000; animation: slideUpFade 0.18s ease;
          overflow: hidden;
        }
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .erp-dropdown-header {
          display: flex; align-items: center; gap: 8px;
          padding: 11px 14px;
          background: linear-gradient(180deg, #f0fdfa 0%, #ccfbf1 100%);
          border-bottom: 1px solid #99f6e4;
          font-size: 11px; font-weight: 800;
          color: var(--nav-accent-dark); text-transform: uppercase;
          letter-spacing: 0.6px;
        }
        .erp-dropdown-header i { color: var(--nav-accent); font-size: 13px; }
        .erp-dropdown-header span:first-of-type { flex: 1; }

        .header-count {
          background: var(--nav-accent); color: white;
          font-size: 10px; padding: 1px 7px;
          border-radius: 10px; font-weight: 700;
        }

        .erp-dropdown-body {
          padding: 6px 0; max-height: 480px; overflow-y: auto;
        }
        .erp-dropdown-body::-webkit-scrollbar { width: 5px; }
        .erp-dropdown-body::-webkit-scrollbar-thumb {
          background: #cbd5e1; border-radius: 10px;
        }

        .erp-dropdown-link {
          padding: 9px 14px; font-size: 13px;
          font-weight: 500; color: #334155;
          cursor: pointer; transition: all 0.15s;
          display: flex; align-items: center; position: relative;
        }
        .dropdown-item-icon {
          font-size: 14px; color: var(--nav-text-muted);
          margin-right: 11px; width: 16px;
          text-align: center; transition: color 0.15s;
        }
        .link-text { flex: 1; }
        .link-shortcut {
          font-size: 10px; color: var(--nav-text-soft);
          background: #f1f5f9; padding: 2px 7px;
          border-radius: 5px;
          font-family: 'Consolas', 'Monaco', monospace;
          font-weight: 600; border: 1px solid var(--nav-border);
          letter-spacing: 0.3px;
        }
        .link-arrow {
          font-size: 11px; color: #cbd5e1;
          margin-left: 8px; opacity: 0;
          transform: translateX(-4px); transition: all 0.15s;
        }
        .erp-dropdown-link:hover {
          background: var(--nav-accent-soft); color: var(--nav-accent-dark);
        }
        .erp-dropdown-link:hover .dropdown-item-icon { color: var(--nav-accent); }
        .erp-dropdown-link:hover .link-shortcut {
          background: #ccfbf1; color: var(--nav-accent-dark); border-color: #99f6e4;
        }
        .erp-dropdown-link:hover .link-arrow {
          opacity: 1; transform: translateX(0); color: var(--nav-accent);
        }

        /* ============================
           RIGHT SIDE (merged sub-header)
           ============================ */
        .erp-nav-right {
          display: flex; align-items: center;
          gap: 0;
          background: var(--nav-bg-soft);
          border: 1px solid var(--nav-border);
          border-radius: 11px;
          padding: 4px 6px;
        }

        .nav-vert-divider {
          width: 1px; height: 28px;
          background: var(--nav-border);
          margin: 0 8px;
        }

        /* Current Page */
        .nav-current-page {
          display: flex; align-items: center; gap: 6px;
          padding: 5px 10px;
          background: var(--nav-accent-soft);
          border: 1px solid rgba(13, 148, 136, 0.25);
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          color: var(--nav-accent-dark);
          letter-spacing: 0.2px;
          max-width: 210px;
        }
        .nav-current-page i {
          font-size: 11px; color: var(--nav-accent);
        }
        .nav-current-page span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Clock + Date Combined */
        .nav-clock {
          display: flex; align-items: center; gap: 8px;
          padding: 2px 4px;
        }
        .nav-clock > i {
          color: var(--nav-text-muted); font-size: 14px;
        }
        .clock-meta {
          display: flex; flex-direction: column;
          line-height: 1.15;
        }
        .clock-time {
          font-family: 'Consolas', 'Monaco', monospace;
          font-size: 12.5px; font-weight: 700;
          color: var(--nav-text); letter-spacing: 0.3px;
        }
        .clock-date {
          font-size: 9px; font-weight: 700;
          color: var(--nav-text-muted); text-transform: uppercase;
          letter-spacing: 0.6px; margin-top: 1px;
        }

        /* User Chip */
        .nav-user-chip {
          display: flex; align-items: center; gap: 8px;
          padding: 2px 8px 2px 2px;
          cursor: pointer;
          border-radius: 8px;
          transition: background 0.2s;
        }
        .nav-user-chip:hover {
          background: #eef2f7;
        }
        .nav-user-avatar {
          width: 30px; height: 30px;
          background: linear-gradient(135deg, #14b8a6 0%, #0f766e 100%);
          color: white; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 13px;
          box-shadow: 0 2px 5px rgba(13, 148, 136, 0.35);
        }
        .nav-user-meta {
          display: flex; flex-direction: column;
          line-height: 1.15;
        }
        .nav-user-name {
          font-size: 12px; font-weight: 700;
          color: var(--nav-text); letter-spacing: 0.2px;
        }
        .nav-user-role {
          font-size: 9px; font-weight: 700;
          color: var(--nav-text-muted); text-transform: uppercase;
          letter-spacing: 0.6px; margin-top: 1px;
        }

        /* Responsive */
        @media (max-width: 1400px) {
          .nav-current-page { max-width: 150px; }
        }

        @media (max-width: 1280px) {
          .module-title-wrap { display: none; }
          .erp-divider:nth-of-type(2) { display: none; }
          .nav-current-page span { display: none; }
          .nav-current-page { padding: 5px 8px; }
        }

        @media (max-width: 1100px) {
          .erp-menu-btn span { display: none; }
          .erp-menu-btn { padding: 8px 10px; }
          .clock-date { display: none; }
          .nav-clock { padding: 0 2px; }
        }

        @media (max-width: 900px) {
          .nav-user-meta { display: none; }
          .nav-vert-divider:last-of-type { display: none; }
        }

        @media (max-width: 768px) {
          .nav-current-page { display: none; }
          .nav-vert-divider:first-of-type { display: none; }
        }

        /* PHONE: hamburger replaces the inline menu bar */
        @media (max-width: 720px) {
          .erp-hamburger { display: flex; }
          .erp-menu-bar { display: none; }
          .erp-divider { display: none; }
          .nav-clock { display: none; }
          .erp-nav-right {
            margin-left: auto;
            border: none; background: transparent; padding: 0;
          }
          .erp-nav-container { padding: 0 12px; }
          .erp-brand { padding: 6px 10px 6px 6px; }
        }
        @media (max-width: 420px) {
          .brand-text { display: none; }
          .nav-user-avatar { width: 32px; height: 32px; }
        }
      `}</style>
    </div>
  );
};

export default ClinicalNavbar;